import React from 'react';
import { Alert, Box, Button, Chip, Divider, Paper, Typography } from '@mui/material';
import { ArrowBack, CheckCircle } from '@mui/icons-material';
import {
  isQuestionVisible,
  isSectionVisible,
  purposeLabel,
  PREVIEW_UNSAFE_RESPONSE_TYPES,
} from '../../../support/assessments';
import AssessmentQuestionInput from '../AssessmentQuestionInput';
import AssessmentResultsSummary from '../results/AssessmentResultsSummary';
import ImportedWidgetsView from '../../DataImport/ImportedWidgetsView';
import { AssessmentLayoutProps } from './types';

/** Dataset refs saved by any `data_import` answers, so the report can re-render their widgets. */
const importedDatasetIds = (
  allQuestions: AssessmentLayoutProps['allQuestions'],
  answerFor: AssessmentLayoutProps['answerFor']
): number[] =>
  allQuestions
    .filter((q) => q.response_type === 'data_import')
    .map((q) => answerFor(q)?.datasetId)
    .filter((id): id is number => typeof id === 'number');

/**
 * The default assessment layout: a single scrolling form inside the normal app shell. This is what
 * every assessment used before layouts existed, so it is the fallback when `metadata.layout` is
 * unset or unrecognised. On submit it shows a thank-you card, plus the computed results summary when
 * the assessment carries a scoring config.
 */
const DefaultAssessmentLayout: React.FC<AssessmentLayoutProps> = ({
  assessment,
  sections,
  questionsBySection,
  allQuestions,
  answers,
  respondent,
  answerFor,
  onChangeFor,
  submitError,
  submitting,
  submitted,
  result,
  report,
  onSubmit,
  onExit,
  previewMode,
}) => {
  if (submitted) {
    const datasetIds = importedDatasetIds(allQuestions, answerFor);
    return (
      <Box maxWidth={result ? 960 : 720} mx="auto" p={3}>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Thank you
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your responses to “{assessment.name}” have been recorded.
          </Typography>
          <Button variant="contained" sx={{ mt: 3 }} onClick={onExit}>
            Back to Assessments
          </Button>
        </Paper>

        {result && (
          <Box mt={3}>
            <AssessmentResultsSummary result={result} report={report} />
          </Box>
        )}

        {datasetIds.map((datasetId) => (
          <Box mt={3} key={datasetId}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Imported data
            </Typography>
            <ImportedWidgetsView datasetId={datasetId} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box maxWidth={820} mx="auto" p={3}>
      <Button startIcon={<ArrowBack />} onClick={onExit} sx={{ mb: 2 }}>
        All Assessments
      </Button>

      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h5">{assessment.name}</Typography>
          <Chip size="small" label={purposeLabel(assessment.purpose)} />
        </Box>
        {!!assessment.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {assessment.description}
          </Typography>
        )}
      </Box>

      {sections
        .filter((section) => isSectionVisible(section, answers))
        .map((section) => {
          const questions = (questionsBySection[section.id] || []).filter((question) =>
            isQuestionVisible(question, answers)
          );
          if (questions.length === 0) return null;

          return (
            <Paper key={section.id} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6">{section.title}</Typography>
              {!!section.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {section.description}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />

              {questions.map((question) => (
                <Box key={question.id} mb={3}>
                  <Typography variant="subtitle2" component="label">
                    {question.text}
                    {question.required && (
                      <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                        *
                      </Typography>
                    )}
                  </Typography>
                  {!!question.help_text && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {question.help_text}
                    </Typography>
                  )}
                  <Box mt={1}>
                    <AssessmentQuestionInput
                      question={question}
                      value={answerFor(question)}
                      onChange={onChangeFor(question)}
                      // In an admin preview, disable inputs that would otherwise
                      // fire real side effects (uploads, Salesforce, data import).
                      disabled={!!previewMode && PREVIEW_UNSAFE_RESPONSE_TYPES.includes(question.response_type)}
                      // Only the coupled `sf_account_file` input needs the full (per-keystroke)
                      // answers map; passing it to every input would defeat the memoization.
                      answersByKey={
                        question.response_type === 'sf_account_file' ? answers : undefined
                      }
                      questions={
                        question.response_type === 'sf_account_file' ? allQuestions : undefined
                      }
                      respondent={respondent}
                    />
                  </Box>
                </Box>
              ))}
            </Paper>
          );
        })}

      {!!submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onExit} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </Box>
    </Box>
  );
};

export default DefaultAssessmentLayout;
